import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, MagnifyingGlass, Pencil, Trash, FilePdf, Link as LinkIcon, Printer, Sparkle, EnvelopeSimple, Paperclip, X } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import { printHTML } from '../utils/printHelper';

// Kategorije djelatnosti klijenata
export const KATEGORIJE_DJELATNOSTI = [
  { value: 'trgovina', label: 'Trgovina', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'ugostiteljstvo', label: 'Ugostiteljstvo', color: 'bg-pink-50 text-pink-700 border-pink-200' },
  { value: 'gradjevina', label: 'Građevina', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'usluge', label: 'Usluge', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { value: 'ostalo', label: 'Ostalo', color: 'bg-slate-50 text-slate-600 border-slate-200' },
];

export const getKategorijaMeta = (v) => KATEGORIJE_DJELATNOSTI.find(k => k.value === v) || null;

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPdvStatus, setFilterPdvStatus] = useState('');
  const [filterAktivan, setFilterAktivan] = useState('');
  const [filterTipLica, setFilterTipLica] = useState('');
  const [filterKategorija, setFilterKategorija] = useState('');
  const [autoCategorizing, setAutoCategorizing] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailClient, setEmailClient] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', poruka: '' });
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchingIRMS, setSearchingIRMS] = useState(false);

  // Config lists from settings
  const [cfgNacionalnosti, setCfgNacionalnosti] = useState([]);
  const [cfgVjere, setCfgVjere] = useState([]);
  
  // Documents state
  const [isDocumentsDialogOpen, setIsDocumentsDialogOpen] = useState(false);
  const [selectedClientForDocs, setSelectedClientForDocs] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isAddDocDialogOpen, setIsAddDocDialogOpen] = useState(false);
  const [docFormData, setDocFormData] = useState({
    tip_dokumenta: 'rjesenje_registracija',
    naziv: '',
    google_drive_url: ''
  });

  const [formData, setFormData] = useState({
    naziv_firme: '',
    skraceni_naziv: '',
    pib: '',
    registarski_broj: '',
    adresa: '',
    grad: '',
    direktor: '',
    jmb_direktora: '',
    nacionalnost: '',
    vjera: '',
    ziro_racun_1: '',
    ziro_racun_2: '',
    ziro_racun_3: '',
    telefon: '',
    email: '',
    website: '',
    djelatnost: '',
    kategorija_djelatnosti: '',
    pdv_status: 'ne-obveznik',
    tip_lica: 'doo',
    napomena: '',
    tagovi: [],
    aktivan: true,
  });

  useEffect(() => {
    fetchClients();
    fetchConfigLists();
  }, [search, filterPdvStatus, filterAktivan, filterTipLica, filterKategorija]);

  const fetchConfigLists = async () => {
    try {
      const r = await api.get('/settings/config-lists');
      setCfgNacionalnosti(r.data.nacionalnosti || []);
      setCfgVjere(r.data.vjere || []);
    } catch {}
  };

  const fetchClients = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterPdvStatus && filterPdvStatus !== 'all') params.pdv_status = filterPdvStatus;
      if (filterAktivan === 'aktivan') params.aktivan = true;
      else if (filterAktivan === 'neaktivan') params.aktivan = false;
      if (filterTipLica && filterTipLica !== 'all') params.tip_lica = filterTipLica;
      if (filterKategorija && filterKategorija !== 'all') params.kategorija_djelatnosti = filterKategorija;

      const response = await api.get('/clients', { params });
      setClients(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju klijenata');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, formData);
        toast.success('Klijent ažuriran');
      } else {
        await api.post('/clients', formData);
        toast.success('Klijent kreiran');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('već postoji')) {
        const errorMsg = error.response.data.detail;
        toast.error(errorMsg, {
          duration: 6000
        });
        // Automatski postavi search na PIB da korisnik vidi postojećeg klijenta
        const pibMatch = errorMsg.match(/PIB-om (\d+)/);
        if (pibMatch) {
          setTimeout(() => {
            setIsDialogOpen(false);
            setSearch(pibMatch[1]);
          }, 1500);
        }
      } else {
        toast.error(error.response?.data?.detail || 'Greška pri čuvanju klijenta');
      }
    }
  };

  const handleDelete = async (clientId) => {
    if (!(await confirmDialog('Da li ste sigurni da želite obrisati ovog klijenta?'))) return;

    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Klijent obrisan');
      fetchClients();
    } catch (error) {
      toast.error('Greška pri brisanju klijenta');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({ ...client });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      naziv_firme: '',
      skraceni_naziv: '',
      pib: '',
      registarski_broj: '',
      adresa: '',
      grad: '',
      direktor: '',
      jmb_direktora: '',
      nacionalnost: '',
      vjera: '',
      ziro_racun_1: '',
      ziro_racun_2: '',
      ziro_racun_3: '',
      telefon: '',
      email: '',
      website: '',
      djelatnost: '',
      kategorija_djelatnosti: '',
      pdv_status: 'ne-obveznik',
      napomena: '',
      tagovi: [],
      aktivan: true,
    });
    setEditingClient(null);
  };

  const handleAutoCategorize = async () => {
    if (!(await confirmDialog('Ovo će automatski dodijeliti kategoriju (trgovina, ugostiteljstvo, građevina, usluge, ostalo) svim klijentima koji je još nemaju, na osnovu polja "Djelatnost". Nastaviti?'))) return;
    setAutoCategorizing(true);
    try {
      const r = await api.post('/clients/auto-categorize', null, { params: { only_empty: true } });
      const d = r.data;
      const by = d.by_kategorija || {};
      toast.success(
        `Kategorizovano ${d.updated} klijenata: Trgovina ${by.trgovina || 0} • Ugostiteljstvo ${by.ugostiteljstvo || 0} • Građevina ${by.gradjevina || 0} • Usluge ${by.usluge || 0} • Ostalo ${by.ostalo || 0}`,
        { duration: 8000 }
      );
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška pri auto-kategorizaciji');
    } finally {
      setAutoCategorizing(false);
    }
  };

  const openEmailDialog = (client) => {
    if (!client.email) {
      toast.error('Klijent nema email adresu. Prvo dodajte email na kartici klijenta.');
      return;
    }
    if (client.aktivan === false) {
      toast.error('Klijent je označen kao neaktivan. Email neće biti poslat.');
      return;
    }
    setEmailClient(client);
    setEmailForm({ subject: '', poruka: '' });
    setEmailAttachments([]);
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim()) { toast.error('Unesite naslov emaila'); return; }
    if (!emailForm.poruka.trim()) { toast.error('Unesite poruku'); return; }
    setSendingEmail(true);
    try {
      const fd = new FormData();
      fd.append('subject', emailForm.subject);
      fd.append('poruka', emailForm.poruka);
      emailAttachments.forEach(f => fd.append('files', f));
      const r = await api.post(`/clients/${emailClient.id}/send-email`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`✅ Email poslat na ${r.data.to_email}${r.data.attachments_count ? ` sa ${r.data.attachments_count} priloga` : ''}`);
      setEmailDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška pri slanju emaila');
    } finally {
      setSendingEmail(false);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const searchIRMS = async () => {
    if (!formData.pib || formData.pib.length < 8) {
      toast.error('Unesite važeći PIB (minimum 8 cifara)');
      return;
    }

    setSearchingIRMS(true);
    try {
      const response = await api.get(`/irms/search/${formData.pib}`);
      if (response.data.found) {
        const irmsData = response.data.data;
        setFormData({
          ...formData,
          naziv_firme: irmsData.naziv_firme || formData.naziv_firme,
          skraceni_naziv: irmsData.skraceni_naziv || formData.skraceni_naziv,
          registarski_broj: irmsData.registarski_broj || formData.registarski_broj,
          adresa: irmsData.adresa || formData.adresa,
          grad: irmsData.grad || formData.grad,
          djelatnost: irmsData.djelatnost || formData.djelatnost,
          direktor: irmsData.direktor || formData.direktor,
          email: irmsData.email || formData.email,
          website: irmsData.website || formData.website,
          telefon: irmsData.telefon || formData.telefon,
          pdv_status: irmsData.pdv_status || formData.pdv_status,
        });
        toast.success('Podaci preuzeti iz IRMS sistema!');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Firma sa ovim PIB-om nije pronađena u IRMS sistemu. Molimo unesite podatke ručno.', {
          duration: 5000
        });
      } else if (error.response?.status === 503 || error.response?.status === 408) {
        toast.error('IRMS sistem je trenutno nedostupan. Molimo unesite podatke ručno.', {
          duration: 6000
        });
      } else {
        toast.error('IRMS sistem je trenutno nedostupan. Možete nastaviti sa ručnim unosom podataka.', {
          duration: 6000
        });
      }
    } finally {
      setSearchingIRMS(false);
    }
  };

  const searchCBCG = () => {
    const mb = formData.registarski_broj || '';
    if (!mb) {
      toast.error('Unesite matični/registarski broj firme');
      return;
    }
    window.open(`https://eportal.cbcg.me/cbcgPregledi/faces/pages/pregledracuni.jspx`, '_blank');
    toast.info('CBCG portal otvoren. Unesite matični broj: ' + mb + ' i kopirajte žiro račune.', { duration: 8000 });
  };

  const handleViewDocuments = async (client) => {
    setSelectedClientForDocs(client);
    try {
      const response = await api.get(`/clients/${client.id}/documents`);
      setDocuments(response.data);
      setIsDocumentsDialogOpen(true);
    } catch (error) {
      toast.error('Greška pri učitavanju dokumenata');
    }
  };

  const handleAddDocument = async () => {
    if (!docFormData.naziv || !docFormData.google_drive_url) {
      toast.error('Unesite naziv i Google Drive link');
      return;
    }

    try {
      await api.post(`/clients/${selectedClientForDocs.id}/documents`, {
        client_id: selectedClientForDocs.id,
        ...docFormData
      });
      toast.success('Dokument dodan');
      setIsAddDocDialogOpen(false);
      setDocFormData({
        tip_dokumenta: 'rjesenje_registracija',
        naziv: '',
        google_drive_url: ''
      });
      
      const response = await api.get(`/clients/${selectedClientForDocs.id}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Greška pri dodavanju dokumenta');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!(await confirmDialog('Da li ste sigurni da želite obrisati ovaj dokument?'))) return;

    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Dokument obrisan');
      
      const response = await api.get(`/clients/${selectedClientForDocs.id}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Greška pri brisanju dokumenta');
    }
  };

  const getTipLabel = (tip) => {
    const labels = {
      'rjesenje_registracija': 'Rješenje o registraciji',
      'rjesenje_pdv': 'Rješenje o PDV-u',
      'ugovor': 'Ugovor',
      'ostalo': 'Ostalo'
    };
    return labels[tip] || tip;
  };

  const handlePrintList = () => {
    const today = new Date().toLocaleDateString('sr-Latn-ME', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const filterDesc = [];
    if (search) filterDesc.push(`Pretraga: "${search}"`);
    if (filterPdvStatus && filterPdvStatus !== 'all') filterDesc.push(`PDV: ${filterPdvStatus === 'obveznik' ? 'Obveznik' : 'Ne-obveznik'}`);
    if (filterTipLica && filterTipLica !== 'all') filterDesc.push(`Tip lica: ${filterTipLica === 'doo' ? 'DOO' : filterTipLica === 'preduzetnik' ? 'Preduzetnik' : 'NVO'}`);
    if (filterAktivan === 'aktivan') filterDesc.push('Status: Aktivan');
    else if (filterAktivan === 'neaktivan') filterDesc.push('Status: Neaktivan');
    if (filterKategorija && filterKategorija !== 'all') {
      const meta = getKategorijaMeta(filterKategorija);
      if (meta) filterDesc.push(`Kategorija: ${meta.label}`);
    }
    const filterLine = filterDesc.length ? `Filteri: ${filterDesc.join(' | ')}` : 'Svi klijenti';

    const escapeHtml = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    const rows = clients.map((c, i) => `
      <tr>
        <td class="num">${i + 1}.</td>
        <td>${escapeHtml(c.skraceni_naziv || c.naziv_firme || '')}</td>
        <td>${escapeHtml(c.pib || '')}</td>
        <td>${escapeHtml(c.grad || '')}</td>
        <td>${escapeHtml(c.direktor || '')}</td>
        <td class="nowrap">${escapeHtml(c.telefon || '')}</td>
        <td class="note">${escapeHtml(c.napomena || '')}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Lista klijenata</title>
<style>
@page { size: A4 landscape; margin: 10mm 10mm 12mm 10mm; }
@media print { html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 10pt; color: #1a1a1a; }
.header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #1e3a8a; padding-bottom: 6px; margin-bottom: 10px; }
.header h1 { font-size: 16pt; color: #1e3a8a; font-weight: 600; letter-spacing: 0.5px; }
.header .meta { text-align: right; font-size: 9pt; color: #475569; line-height: 1.5; }
.subhdr { font-size: 9pt; color: #64748b; margin-bottom: 6px; display: flex; justify-content: space-between; }
table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; }
tr { page-break-inside: avoid; }
th { background: #1e3a8a; color: #fff; font-weight: 600; font-size: 9pt; text-transform: uppercase; letter-spacing: 0.3px; padding: 6px 8px; text-align: left; border: 1px solid #1e3a8a; }
td { padding: 5px 8px; border: 1px solid #cbd5e1; font-size: 9.5pt; vertical-align: top; }
tbody tr:nth-child(even) td { background: #f8fafc; }
td.num { width: 32px; text-align: center; color: #64748b; font-weight: 600; }
td.nowrap { white-space: nowrap; }
td.note { font-size: 8.5pt; color: #475569; font-style: italic; max-width: 200px; }
.footer { margin-top: 12px; font-size: 8.5pt; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 6px; }
</style></head><body>
<div class="header">
  <h1>LISTA KLIJENATA</h1>
  <div class="meta"><strong>Datum:</strong> ${today}<br><strong>Ukupno:</strong> ${clients.length} klijenata</div>
</div>
<div class="subhdr"><span>${filterLine}</span><span></span></div>
<table>
  <thead>
    <tr>
      <th style="width:32px">#</th>
      <th>Naziv firme</th>
      <th style="width:80px">PIB</th>
      <th style="width:120px">Grad</th>
      <th style="width:180px">Direktor</th>
      <th style="width:110px">Telefon</th>
      <th>Napomena</th>
    </tr>
  </thead>
  <tbody>
    ${rows || '<tr><td colspan="7" style="text-align:center; padding:20px; color:#94a3b8;">Nema klijenata za prikaz</td></tr>'}
  </tbody>
</table>
<div class="footer">FINANCE GROUP CRM &mdash; izvjestaj generisan ${today}</div>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
    printHTML(html);
  };


  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex-1 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Pretraži po nazivu, PIB-u, direktoru..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white border-slate-300 text-sm"
              data-testid="clients-search-input"
            />
          </div>
          <Select value={filterPdvStatus} onValueChange={setFilterPdvStatus}>
            <SelectTrigger className="w-[160px] bg-white border-slate-300" data-testid="clients-filter-pdv">
              <SelectValue placeholder="PDV Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">PDV: Svi</SelectItem>
              <SelectItem value="obveznik">PDV Obveznik</SelectItem>
              <SelectItem value="ne-obveznik">Ne-obveznik</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTipLica} onValueChange={setFilterTipLica}>
            <SelectTrigger className="w-[160px] bg-white border-slate-300" data-testid="clients-filter-tip-lica">
              <SelectValue placeholder="Tip lica" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tip: Svi</SelectItem>
              <SelectItem value="doo">DOO</SelectItem>
              <SelectItem value="preduzetnik">Preduzetnik</SelectItem>
              <SelectItem value="nvo">NVO</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAktivan} onValueChange={setFilterAktivan}>
            <SelectTrigger className="w-[160px] bg-white border-slate-300" data-testid="clients-filter-aktivan">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Status: Svi</SelectItem>
              <SelectItem value="aktivan">Aktivan</SelectItem>
              <SelectItem value="neaktivan">Neaktivan</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterKategorija} onValueChange={setFilterKategorija}>
            <SelectTrigger className="w-[180px] bg-white border-slate-300" data-testid="clients-filter-kategorija">
              <SelectValue placeholder="Kategorija" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Kategorija: Sve</SelectItem>
              {KATEGORIJE_DJELATNOSTI.map(k => (
                <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleAutoCategorize}
            variant="outline"
            className="rounded-md px-4 py-2 font-medium transition-colors border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="clients-auto-categorize-button"
            disabled={autoCategorizing}
            title="Automatski dodijeli kategoriju klijentima koji je nemaju, na osnovu polja Djelatnost"
          >
            <Sparkle size={18} weight="bold" className="mr-2" />
            {autoCategorizing ? 'Kategorizujem...' : 'Auto-kategorizuj'}
          </Button>
          <Button
            onClick={handlePrintList}
            variant="outline"
            className="rounded-md px-4 py-2 font-medium transition-colors border-slate-300 hover:bg-slate-50"
            data-testid="clients-print-button"
            disabled={clients.length === 0}
          >
            <Printer size={18} weight="bold" className="mr-2" />
            Štampaj listu
          </Button>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreateDialog}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium transition-colors"
                data-testid="clients-add-button"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Dodaj klijenta
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium text-slate-900">
                {editingClient ? 'Uredi klijenta' : 'Dodaj novog klijenta'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="client-form">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-xs font-semibold tracking-wider uppercase text-blue-700 mb-2">
                  IRMS Pretraga - Automatsko popunjavanje (Opciono)
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Unesite PIB"
                    value={formData.pib}
                    onChange={(e) => setFormData({ ...formData, pib: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-pib-irms-input"
                  />
                  <Button
                    type="button"
                    onClick={searchIRMS}
                    disabled={searchingIRMS}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium whitespace-nowrap"
                    data-testid="client-search-irms-button"
                  >
                    {searchingIRMS ? 'Pretraživanje...' : 'Pretraži IRMS'}
                  </Button>
                </div>
                <p className="text-xs text-slate-600 mt-2">
                  Pokušajte automatski popuniti podatke iz poreske uprave. Ako IRMS ne radi, nastavite sa ručnim unosom ispod.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Naziv firme *</Label>
                  <Input
                    required
                    value={formData.naziv_firme}
                    onChange={(e) => setFormData({ ...formData, naziv_firme: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-naziv-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Skraćeni naziv</Label>
                  <Input
                    value={formData.skraceni_naziv}
                    onChange={(e) => setFormData({ ...formData, skraceni_naziv: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-skraceni-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">PIB *</Label>
                  <Input
                    required
                    value={formData.pib}
                    onChange={(e) => setFormData({ ...formData, pib: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-pib-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Registarski broj *</Label>
                  <Input
                    required
                    value={formData.registarski_broj}
                    onChange={(e) => setFormData({ ...formData, registarski_broj: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-reg-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Adresa</Label>
                  <Input
                    value={formData.adresa}
                    onChange={(e) => setFormData({ ...formData, adresa: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-adresa-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Grad</Label>
                  <Input
                    value={formData.grad}
                    onChange={(e) => setFormData({ ...formData, grad: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-grad-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Direktor</Label>
                  <Input
                    value={formData.direktor}
                    onChange={(e) => setFormData({ ...formData, direktor: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-direktor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">JMB Direktora</Label>
                  <Input
                    value={formData.jmb_direktora}
                    onChange={(e) => setFormData({ ...formData, jmb_direktora: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-jmb-input"
                    placeholder="13 cifara"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Nacionalnost</Label>
                  <Select value={formData.nacionalnost || ''} onValueChange={(value) => setFormData({ ...formData, nacionalnost: value })}>
                    <SelectTrigger className="bg-white border-slate-300 text-sm" data-testid="client-nacionalnost-select">
                      <SelectValue placeholder="Izaberite nacionalnost" />
                    </SelectTrigger>
                    <SelectContent>
                      {cfgNacionalnosti.map(n => (
                        <SelectItem key={n} value={n.toLowerCase()}>{n.charAt(0).toUpperCase() + n.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Vjera</Label>
                  <Select value={formData.vjera || ''} onValueChange={(value) => setFormData({ ...formData, vjera: value })}>
                    <SelectTrigger className="bg-white border-slate-300 text-sm" data-testid="client-vjera-select">
                      <SelectValue placeholder="Izaberite vjeru" />
                    </SelectTrigger>
                    <SelectContent>
                      {cfgVjere.map(v => (
                        <SelectItem key={v} value={v.toLowerCase()}>{v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Telefon</Label>
                  <Input
                    value={formData.telefon}
                    onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-telefon-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-website-input"
                  />
                </div>

                {/* Žiro računi */}
                <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Žiro računi firme</Label>
                    <Button type="button" variant="outline" size="sm" onClick={searchCBCG} className="text-xs h-7 px-2" data-testid="cbcg-search-btn">
                      Otvori CBCG portal
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <Input
                      value={formData.ziro_racun_1}
                      onChange={(e) => setFormData({ ...formData, ziro_racun_1: e.target.value })}
                      className="bg-white border-slate-300 text-sm"
                      placeholder="Žiro račun 1"
                      data-testid="client-ziro1-input"
                    />
                    <Input
                      value={formData.ziro_racun_2}
                      onChange={(e) => setFormData({ ...formData, ziro_racun_2: e.target.value })}
                      className="bg-white border-slate-300 text-sm"
                      placeholder="Žiro račun 2"
                      data-testid="client-ziro2-input"
                    />
                    <Input
                      value={formData.ziro_racun_3}
                      onChange={(e) => setFormData({ ...formData, ziro_racun_3: e.target.value })}
                      className="bg-white border-slate-300 text-sm"
                      placeholder="Žiro račun 3"
                      data-testid="client-ziro3-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Djelatnost</Label>
                  <Input
                    value={formData.djelatnost}
                    onChange={(e) => setFormData({ ...formData, djelatnost: e.target.value })}
                    className="bg-white border-slate-300 text-sm"
                    data-testid="client-djelatnost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Kategorija djelatnosti</Label>
                  <Select
                    value={formData.kategorija_djelatnosti || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, kategorija_djelatnosti: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="bg-white border-slate-300" data-testid="client-kategorija-select">
                      <SelectValue placeholder="Nije dodijeljena" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nije dodijeljena —</SelectItem>
                      {KATEGORIJE_DJELATNOSTI.map(k => (
                        <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Za segmentaciju obavještenja (Email/Viber) po tipu djelatnosti.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">PDV Status *</Label>
                  <Select
                    value={formData.pdv_status}
                    onValueChange={(value) => setFormData({ ...formData, pdv_status: value })}
                  >
                    <SelectTrigger className="bg-white border-slate-300" data-testid="client-pdv-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="obveznik">PDV Obveznik</SelectItem>
                      <SelectItem value="ne-obveznik">Ne-obveznik</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Tip pravnog lica *</Label>
                  <Select
                    value={formData.tip_lica || 'doo'}
                    onValueChange={(value) => setFormData({ ...formData, tip_lica: value })}
                  >
                    <SelectTrigger className="bg-white border-slate-300" data-testid="client-tip-lica-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="doo">DOO</SelectItem>
                      <SelectItem value="preduzetnik">Preduzetnik</SelectItem>
                      <SelectItem value="nvo">NVO</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Napomena</Label>
                <Input
                  value={formData.napomena}
                  onChange={(e) => setFormData({ ...formData, napomena: e.target.value })}
                  className="bg-white border-slate-300 text-sm"
                  data-testid="client-napomena-input"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  checked={formData.aktivan}
                  onChange={(e) => setFormData({ ...formData, aktivan: e.target.checked })}
                  className="rounded border-slate-300 w-4 h-4"
                  data-testid="client-aktivan-checkbox"
                />
                <Label className="text-sm text-slate-700 cursor-pointer">Aktivan klijent</Label>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-md px-4 py-2 font-medium"
                  data-testid="client-cancel-button"
                >
                  Odustani
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                  data-testid="client-submit-button"
                >
                  {editingClient ? 'Ažuriraj' : 'Kreiraj'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Učitavanje...</p>
        </div>
      ) : clients.length === 0 ? (
        <Card className="p-8 text-center" data-testid="clients-empty-state">
          <p className="text-slate-500">Nema klijenata</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600" data-testid="clients-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Naziv</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">PIB</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">PDV Broj</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Direktor</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Grad</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Kategorija</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">PDV Status</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Status</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`client-row-${client.id}`}>
                    <td className="px-5 py-3" data-testid={`client-naziv-${client.id}`}>
                      {client.skraceni_naziv || client.naziv_firme}
                    </td>
                    <td className="px-5 py-3" data-testid={`client-pib-${client.id}`}>{client.pib}</td>
                    <td className="px-5 py-3" data-testid={`client-pdv-broj-${client.id}`}>
                      {client.pdv_status === 'obveznik' ? <span className="font-medium text-blue-700">ME{client.pib}</span> : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-5 py-3">{client.direktor || 'N/A'}</td>
                    <td className="px-5 py-3">{client.grad || 'N/A'}</td>
                    <td className="px-5 py-3">
                      {(() => {
                        const meta = getKategorijaMeta(client.kategorija_djelatnosti);
                        return meta ? (
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs font-medium border ${meta.color}`}
                            data-testid={`client-kategorija-${client.id}`}
                          >
                            {meta.label}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400" data-testid={`client-kategorija-${client.id}`}>—</span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          client.pdv_status === 'obveznik'
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'bg-slate-50 text-slate-700 border border-slate-200'
                        }`}
                        data-testid={`client-pdv-${client.id}`}
                      >
                        {client.pdv_status === 'obveznik' ? 'PDV Obveznik' : 'Ne-obveznik'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          client.aktivan !== false
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-red-50 text-red-600 border border-red-200'
                        }`}
                        data-testid={`client-status-${client.id}`}
                      >
                        {client.aktivan !== false ? 'Aktivan' : 'Neaktivan'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEmailDialog(client)}
                          className="p-2 hover:bg-emerald-100 rounded transition-colors"
                          title="Pošalji email sa prilozima"
                          data-testid={`client-email-${client.id}`}
                        >
                          <EnvelopeSimple size={16} className="text-emerald-600" />
                        </button>
                        <button
                          onClick={() => handleViewDocuments(client)}
                          className="p-2 hover:bg-blue-100 rounded transition-colors"
                          title="Dokumenti"
                          data-testid={`client-docs-${client.id}`}
                        >
                          <LinkIcon size={16} className="text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleEdit(client)}
                          className="p-2 hover:bg-slate-200 rounded transition-colors"
                          data-testid={`client-edit-${client.id}`}
                        >
                          <Pencil size={16} className="text-slate-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(client.id)}
                          className="p-2 hover:bg-red-100 rounded transition-colors"
                          data-testid={`client-delete-${client.id}`}
                        >
                          <Trash size={16} className="text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium text-slate-900 flex items-center gap-2">
              <EnvelopeSimple size={20} className="text-emerald-600" />
              Pošalji Email — {emailClient?.skraceni_naziv || emailClient?.naziv_firme}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
              <div><span className="text-slate-500">Prima:</span> <strong className="text-slate-800">{emailClient?.email || '-'}</strong></div>
              {emailClient?.direktor && <div className="text-xs text-slate-500 mt-0.5">Direktor: {emailClient.direktor}</div>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Naslov *</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="npr: Obavještenje o novoj zakonskoj obavezi..."
                className="bg-white border-slate-300 text-sm"
                data-testid="send-email-subject-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Poruka *</Label>
              <textarea
                value={emailForm.poruka}
                onChange={(e) => setEmailForm({ ...emailForm, poruka: e.target.value })}
                placeholder="Napišite poruku..."
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[140px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="send-email-poruka-input"
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 flex items-center gap-1.5">
                  <Paperclip size={13} weight="bold" />
                  Prilozi (bilo koji tip — ukupno max 15 MB)
                </Label>
                {emailAttachments.length > 0 && (
                  <span className="text-xs text-slate-500">
                    {emailAttachments.length} fajl(a) • {(emailAttachments.reduce((s, f) => s + f.size, 0) / (1024 * 1024)).toFixed(2)} MB
                  </span>
                )}
              </div>
              <label
                htmlFor="send-email-file-input"
                className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-md px-3 py-3 cursor-pointer hover:bg-slate-50 transition"
                data-testid="send-email-attach-dropzone"
              >
                <Paperclip size={16} className="text-slate-500" />
                <span className="text-sm text-slate-600">Kliknite da dodate više fajlova (PDF, Word, Excel, slike...)</span>
              </label>
              <input
                id="send-email-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const list = Array.from(e.target.files || []);
                  const total = [...emailAttachments, ...list].reduce((s, f) => s + f.size, 0);
                  if (total > 15 * 1024 * 1024) {
                    toast.error('Ukupna veličina priloga prelazi 15 MB.');
                    e.target.value = '';
                    return;
                  }
                  setEmailAttachments(prev => [...prev, ...list]);
                  e.target.value = '';
                }}
                data-testid="send-email-file-input"
              />
              {emailAttachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {emailAttachments.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-1.5 text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Paperclip size={13} className="text-slate-400 shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-xs text-slate-400 shrink-0">{(f.size / 1024).toFixed(1)} KB</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEmailAttachments(prev => prev.filter((_, idx) => idx !== i))}
                        className="p-1 hover:bg-red-50 rounded text-red-500 shrink-0"
                        data-testid={`send-email-remove-file-${i}`}
                        title="Ukloni fajl"
                      >
                        <X size={13} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEmailDialogOpen(false)}
                className="rounded-md px-4 py-2 font-medium"
                data-testid="send-email-cancel-button"
                disabled={sendingEmail}
              >
                Odustani
              </Button>
              <Button
                type="button"
                onClick={handleSendEmail}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-4 py-2 font-medium"
                data-testid="send-email-submit-button"
                disabled={sendingEmail}
              >
                <EnvelopeSimple size={16} className="mr-2" />
                {sendingEmail ? 'Šaljem...' : 'Pošalji Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={isDocumentsDialogOpen} onOpenChange={setIsDocumentsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="documents-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium text-slate-900">
              Dokumenti - {selectedClientForDocs?.skraceni_naziv || selectedClientForDocs?.naziv_firme}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">{documents.length} dokument(a)</p>
              <Button
                onClick={() => setIsAddDocDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                data-testid="add-document-button"
              >
                <Plus size={16} className="mr-2" />
                Dodaj dokument
              </Button>
            </div>

            {documents.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-slate-500">Nema dokumenata za ovog klijenta</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <Card key={doc.id} className="p-4 hover:shadow-sm transition-shadow" data-testid={`document-${doc.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center flex-shrink-0">
                          <FilePdf size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{doc.naziv}</p>
                          <p className="text-xs text-slate-500">{getTipLabel(doc.tip_dokumenta)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={doc.google_drive_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-blue-100 rounded transition-colors"
                          title="Otvori u Google Drive"
                          data-testid={`document-link-${doc.id}`}
                        >
                          <LinkIcon size={16} className="text-blue-600" />
                        </a>
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="p-2 hover:bg-red-100 rounded transition-colors"
                          data-testid={`document-delete-${doc.id}`}
                        >
                          <Trash size={16} className="text-red-600" />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={isAddDocDialogOpen} onOpenChange={setIsAddDocDialogOpen}>
        <DialogContent data-testid="add-document-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium text-slate-900">Dodaj dokument</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Tip dokumenta</Label>
              <Select value={docFormData.tip_dokumenta} onValueChange={(value) => setDocFormData({ ...docFormData, tip_dokumenta: value })}>
                <SelectTrigger className="bg-white border-slate-300" data-testid="doc-tip-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rjesenje_registracija">Rješenje o registraciji</SelectItem>
                  <SelectItem value="rjesenje_pdv">Rješenje o PDV-u</SelectItem>
                  <SelectItem value="ugovor">Ugovor</SelectItem>
                  <SelectItem value="ostalo">Ostalo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Naziv dokumenta *</Label>
              <Input
                value={docFormData.naziv}
                onChange={(e) => setDocFormData({ ...docFormData, naziv: e.target.value })}
                placeholder="npr. Rješenje o registraciji 2026"
                className="bg-white border-slate-300 text-sm"
                data-testid="doc-naziv-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Google Drive Link *</Label>
              <Input
                value={docFormData.google_drive_url}
                onChange={(e) => setDocFormData({ ...docFormData, google_drive_url: e.target.value })}
                placeholder="https://drive.google.com/..."
                className="bg-white border-slate-300 text-sm"
                data-testid="doc-url-input"
              />
            </div>
            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setIsAddDocDialogOpen(false)}
                className="rounded-md px-4 py-2 font-medium"
              >
                Odustani
              </Button>
              <Button
                onClick={handleAddDocument}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                data-testid="doc-submit-button"
              >
                Dodaj
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
